import React, { useMemo, useState } from 'react';
import { LandmarkIcon, CreditCardIcon, UserRoundIcon, PhoneIcon, FileTextIcon, ShieldCheckIcon, ChevronDownIcon, CheckIcon, WalletIcon, LockKeyholeIcon, CoinsIcon } from 'lucide-react';
import { StatusBar } from './StatusBar';
import { ScreenHeader } from './ScreenHeader';
const BANKS = ['Bank of Ceylon', "People's Bank", 'Commercial Bank of Ceylon', 'Hatton National Bank', 'Sampath Bank', 'Seylan Bank', 'NDB Bank', 'DFCC Bank', 'Nations Trust Bank', 'Pan Asia Bank'];
type SourceAccount = {
  id: string;
  type: string;
  number: string;
  balance: string;
  icon: React.ReactNode;
};
const SOURCE_ACCOUNTS: SourceAccount[] = [{
  id: 'sav',
  type: 'Savings',
  number: '0123 •••• 8910',
  balance: 'LKR 482,650.00',
  icon: <CoinsIcon size={18} data-id="element-687" />
}, {
  id: 'cur',
  type: 'Current',
  number: '0456 •••• 2245',
  balance: 'LKR 128,400.00',
  icon: <WalletIcon size={18} data-id="element-688" />
}, {
  id: 'fd',
  type: 'Fixed Deposit',
  number: '0789 •••• 5512',
  balance: 'LKR 2,100,000.00',
  icon: <LockKeyholeIcon size={18} data-id="element-689" />
}];
export type TransferMode = 'own' | 'another';
export function TransferFormScreen({
  mode,
  onBack,
  onVerify
}: {
  mode: TransferMode;
  onBack: () => void;
  onVerify: (summary: TransferSummary) => void;
}) {
  const [sourceId, setSourceId] = useState<string>(SOURCE_ACCOUNTS[0].id);
  const [bank, setBank] = useState('');
  const [targetOwn, setTargetOwn] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [senderMobile, setSenderMobile] = useState('');
  const [saveRecipient, setSaveRecipient] = useState(false);
  const isAnother = mode === 'another';
  const canVerify = useMemo(() => {
    if (!amount || Number(amount.replace(/,/g, '')) <= 0) return false;
    if (isAnother) {
      return !!(bank && accountNo && name);
    }
    return !!targetOwn && targetOwn !== sourceId;
  }, [amount, bank, accountNo, name, targetOwn, sourceId, isAnother]);
  const source = SOURCE_ACCOUNTS.find(a => a.id === sourceId)!;
  const ownTargets = SOURCE_ACCOUNTS.filter(a => a.id !== sourceId);
  const ownTarget = ownTargets.find(a => a.id === targetOwn);
  return <div className="relative flex flex-col w-full h-full overflow-hidden" style={{
    backgroundColor: '#F4F7FA'
  }} data-id="element-690">
      <StatusBar data-id="element-691" />
      <ScreenHeader title={isAnother ? 'Transfer to Another Bank' : 'Transfer Between Accounts'} subtitle="Step 1 of 2 · Recipient & amount" onBack={onBack} data-id="element-692" />

      <div className="flex-1 overflow-y-auto pb-4" data-id="element-693">
        {/* Source account — horizontal scroll */}
        <div className="px-5 mb-4" data-id="element-694">
          <SectionLabel data-id="element-695">Transfer from</SectionLabel>
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-5 px-5" style={{
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch'
        }} data-id="element-696">
            {SOURCE_ACCOUNTS.map(acc => {
            const active = acc.id === sourceId;
            return <button key={acc.id} type="button" onClick={() => setSourceId(acc.id)} className="shrink-0 rounded-2xl text-left transition-all active:scale-[0.98]" style={{
              width: 200,
              padding: '14px 14px',
              backgroundColor: active ? '#009AC7' : '#FFFFFF',
              color: active ? '#FFFFFF' : '#1A1A1A',
              border: active ? '1.5px solid #009AC7' : '1.5px solid #E5EAF0',
              boxShadow: active ? '0 10px 24px -10px rgba(0,154,199,0.55)' : '0 1px 2px rgba(15,23,42,0.03)'
            }} data-id="element-697">
                  <div className="flex items-center gap-2" data-id="element-698">
                    <span className="flex items-center justify-center rounded-lg" style={{
                  width: 30,
                  height: 30,
                  backgroundColor: active ? 'rgba(255,255,255,0.18)' : '#E6F7FB',
                  color: active ? '#FFFFFF' : '#009AC7'
                }} data-id="element-699">
                      {acc.icon}
                    </span>
                    <span style={{
                  fontSize: 12,
                  fontWeight: 700,
                  opacity: active ? 0.95 : 1
                }} data-id="element-700">
                      {acc.type}
                    </span>
                    {active && <span className="ml-auto inline-flex items-center justify-center rounded-full bg-white" style={{
                  width: 18,
                  height: 18
                }} data-id="element-701">
                        <CheckIcon size={11} color="#009AC7" strokeWidth={3.5} data-id="element-702" />
                      </span>}
                  </div>
                  <div className="mt-2" style={{
                fontSize: 11,
                fontWeight: 500,
                opacity: active ? 0.85 : 0.7
              }} data-id="element-703">
                    {acc.number}
                  </div>
                  <div className="mt-1 tracking-tight" style={{
                fontSize: 15,
                fontWeight: 700
              }} data-id="element-704">
                    {acc.balance}
                  </div>
                </button>;
          })}
          </div>
        </div>

        {/* Amount — hero input */}
        <div className="px-5 mb-5" data-id="element-705">
          <SectionLabel data-id="element-706">Amount</SectionLabel>
          <div className="flex items-baseline gap-2 rounded-3xl bg-white px-5 py-5" style={{
          border: '1.5px solid #E5EAF0'
        }} data-id="element-707">
            <span style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#64748B',
            letterSpacing: '0.04em'
          }} data-id="element-708">
              LKR
            </span>
            <input inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value.replace(/[^0-9.]/g, ''))} placeholder="0.00" className="flex-1 min-w-0 bg-transparent outline-none tracking-tight" style={{
            fontSize: 30,
            fontWeight: 700,
            color: '#1A1A1A'
          }} data-id="element-709" />
          </div>
        </div>

        {/* Recipient details */}
        <div className="px-5 mb-5" data-id="element-710">
          <SectionLabel data-id="element-711">Recipient details</SectionLabel>
          <div className="flex flex-col gap-3" data-id="element-712">
            {isAnother ? <>
                <SelectField label="Recipient's Bank" icon={<LandmarkIcon size={18} data-id="element-714" />} value={bank} onChange={setBank} placeholder="Select bank" options={BANKS} data-id="element-713" />
                <TextField label="Account Number" icon={<CreditCardIcon size={18} data-id="element-716" />} value={accountNo} onChange={setAccountNo} placeholder="e.g. 8001 2345 6789" inputMode="numeric" data-id="element-715" />
                <TextField label="Recipient's Name" icon={<UserRoundIcon size={18} data-id="element-718" />} value={name} onChange={setName} placeholder="As shown on their account" data-id="element-717" />
              </> : <SelectField label="Transfer to" icon={<WalletIcon size={18} data-id="element-720" />} value={targetOwn} onChange={setTargetOwn} placeholder="Select target account" options={ownTargets.map(a => `${a.type} · ${a.number}`)} valueMap={ownTargets.map(a => a.id)} data-id="element-719" />}
          </div>
        </div>

        {/* Additional details */}
        <div className="px-5 mb-5" data-id="element-721">
          <SectionLabel data-id="element-722">Additional details</SectionLabel>
          <div className="flex flex-col gap-3" data-id="element-723">
            <TextField label="Reference" icon={<FileTextIcon size={18} data-id="element-725" />} value={reference} onChange={setReference} placeholder="e.g. Rent for November" data-id="element-724" />
            <TextField label="Sender Mobile" icon={<PhoneIcon size={18} data-id="element-727" />} value={senderMobile} onChange={setSenderMobile} placeholder="+94 7X XXX XXXX" inputMode="tel" optional data-id="element-726" />
          </div>

          {isAnother && <button type="button" onClick={() => setSaveRecipient(v => !v)} className="mt-3 w-full flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-left transition-colors active:bg-neutral-50" style={{
          border: '1px solid #E5EAF0'
        }} data-id="element-728">
              <span className="shrink-0 flex items-center justify-center rounded-md transition-all" style={{
            width: 22,
            height: 22,
            backgroundColor: saveRecipient ? '#009AC7' : '#FFFFFF',
            border: saveRecipient ? '1.5px solid #009AC7' : '1.5px solid #C0C0C0'
          }} data-id="element-729">
                {saveRecipient && <CheckIcon size={13} color="#FFFFFF" strokeWidth={3} data-id="element-730" />}
              </span>
              <div className="flex-1" data-id="element-731">
                <div style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: '#1A1A1A'
            }} data-id="element-732">
                  Save payment receiver details
                </div>
                <div className="mt-0.5" style={{
              fontSize: 11.5,
              color: '#64748B',
              lineHeight: 1.45
            }} data-id="element-733">
                  Re-use this recipient for future transfers
                </div>
              </div>
            </button>}
        </div>
      </div>

      {/* Sticky footer with Verify CTA */}
      <div className="px-5 pt-3 pb-3" style={{
      backgroundColor: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderTop: '1px solid #E5EAF0'
    }} data-id="element-734">
        <button type="button" disabled={!canVerify} onClick={() => canVerify && onVerify({
        mode,
        source: `${source.type} · ${source.number}`,
        recipient: isAnother ? `${name} · ${bank}` : ownTarget ? `${ownTarget.type} · ${ownTarget.number}` : '',
        accountNo: isAnother ? accountNo : ownTarget?.number ?? '',
        amount: amount || '0.00',
        reference
      })} className="w-full rounded-2xl text-white font-semibold transition-all active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed" style={{
        backgroundColor: '#009AC7',
        minHeight: 56,
        fontSize: 16,
        boxShadow: canVerify ? '0 12px 28px -10px rgba(0,154,199,0.55)' : 'none',
        letterSpacing: '0.01em'
      }} data-id="element-735">
          Verify Transfer
        </button>
        <div className="flex items-center justify-center gap-1.5 mt-2.5" data-id="element-736">
          <ShieldCheckIcon size={12} style={{
          color: '#009AC7'
        }} data-id="element-737" />
          <span style={{
          fontSize: 11,
          color: '#525252',
          fontWeight: 500
        }} data-id="element-738">
            Protected by Central Bank of Sri Lanka
          </span>
        </div>
      </div>
      <div className="flex justify-center pb-2 bg-white" data-id="element-739">
        <div className="w-[120px] h-[5px] rounded-full bg-neutral-900/80" aria-hidden="true" data-id="element-740" />
      </div>
    </div>;
}
export type TransferSummary = {
  mode: TransferMode;
  source: string;
  recipient: string;
  accountNo: string;
  amount: string;
  reference: string;
};
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="mb-2.5 px-0.5" style={{
    fontSize: 12.5,
    fontWeight: 700,
    color: '#1A1A1A',
    letterSpacing: '0.02em'
  }} data-id="element-741">
      {children}
    </div>;
}
function TextField({
  label,
  icon,
  value,
  onChange,
  placeholder,
  inputMode,
  optional
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'tel' | 'numeric' | 'decimal';
  optional?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const filled = value.length > 0;
  return <div data-id="element-742">
      <div className="mb-1.5 flex items-center gap-1.5" data-id="element-743">
        <span style={{
        fontSize: 12,
        fontWeight: 600,
        color: '#1A1A1A',
        letterSpacing: '0.02em'
      }} data-id="element-744">
          {label}
        </span>
        {optional && <span style={{
        fontSize: 10.5,
        fontWeight: 600,
        color: '#94A3B8',
        letterSpacing: '0.03em'
      }} data-id="element-745">
            (optional)
          </span>}
      </div>
      <div className="flex items-center gap-2.5 px-3.5 rounded-2xl transition-all" style={{
      minHeight: 54,
      backgroundColor: focused ? '#FFFFFF' : '#FFFFFF',
      border: focused ? '1.5px solid #009AC7' : filled ? '1.5px solid #CBD5E1' : '1.5px solid #E5EAF0',
      boxShadow: focused ? '0 0 0 4px rgba(0,154,199,0.12)' : 'none'
    }} data-id="element-746">
        <span className="shrink-0 flex items-center justify-center" style={{
        color: focused ? '#009AC7' : '#64748B'
      }} data-id="element-747">
          {icon}
        </span>
        <input inputMode={inputMode} value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} placeholder={placeholder} className="flex-1 min-w-0 bg-transparent outline-none" style={{
        fontSize: 15,
        color: '#1A1A1A',
        minHeight: 50,
        fontWeight: 500
      }} data-id="element-748" />
        {filled && <CheckIcon size={16} style={{
        color: '#10B981'
      }} className="shrink-0" aria-hidden="true" data-id="element-749" />}
      </div>
    </div>;
}
function SelectField({
  label,
  icon,
  value,
  onChange,
  placeholder,
  options,
  valueMap
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: string[];
  valueMap?: string[];
}) {
  const filled = value.length > 0;
  return <div data-id="element-750">
      <div className="mb-1.5" style={{
      fontSize: 12,
      fontWeight: 600,
      color: '#1A1A1A',
      letterSpacing: '0.02em'
    }} data-id="element-751">
        {label}
      </div>
      <div className="relative flex items-center gap-2.5 px-3.5 rounded-2xl transition-all" style={{
      minHeight: 54,
      backgroundColor: '#FFFFFF',
      border: filled ? '1.5px solid #CBD5E1' : '1.5px solid #E5EAF0'
    }} data-id="element-752">
        <span className="shrink-0 flex items-center justify-center" style={{
        color: filled ? '#009AC7' : '#64748B'
      }} data-id="element-753">
          {icon}
        </span>
        <select value={value} onChange={e => onChange(e.target.value)} className="flex-1 min-w-0 bg-transparent outline-none appearance-none pr-5" style={{
        fontSize: 15,
        color: filled ? '#1A1A1A' : '#94A3B8',
        minHeight: 50,
        fontWeight: 500
      }} data-id="element-754">
          <option value="" data-id="element-755">{placeholder}</option>
          {options.map((opt, i) => <option key={opt} value={valueMap ? valueMap[i] : opt} data-id="element-756">
              {opt}
            </option>)}
        </select>
        <ChevronDownIcon size={16} style={{
        color: '#64748B'
      }} className="absolute right-3 pointer-events-none" data-id="element-757" />
      </div>
    </div>;
}